import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import BACKEND_URL from '../config';
import Cookies from 'js-cookie';
import AIOperatorMessages from '../../components/adminPanel/AIOperatorMessages';
import HumanSuggestedMessages from '../../components/adminPanel/HumanSuggestedMessages';
import CharacteristicCorrections from '../../components/adminPanel/CharacteristicCorrections';
import DocumentationFeedback from '../../components/adminPanel/DocumentationFeedback';
import AnalyticsTab from '../../components/adminPanel/Analytics/AnalyticsTab';
import PhasesRolesManager from '../../components/adminPanel/PhasesRolesManager';
import ExternalAPI from '../components/ExternalAPI';
import { VictoryBar, VictoryChart, VictoryAxis, VictoryTheme } from 'victory';

interface VNOGUser {
    id: number;
    account_id: number;
    email: string;
    registry_type: 'admin' | 'user';
    created_at: string;
    is_staff: boolean;
}

interface UserInvitation {
    id: number;
    email: string;
    token: string;
    enabled: boolean;
    status: string;
    created_at: string;
    created_by_email: string;
    invitation_link: string;
}

interface Event {
    id: number;
    order?: number;
    event_type: string;
    message_content?: string;
    message_sender?: string;
    adjusted_start_time?: string;
    start_time?: string;
    end_time?: string;
}

interface Situation {
    id: number;
    name?: string;
    gms_id?: string | null;
    archived?: boolean;
    event_log?: Event[];
}

// Per-event-type label + pill colours + left-border accent used to render
// situation event rows. Sender is consulted only as a tiebreaker for the
// generic 'message' bucket so AI Operator messages keep their own colour.
const describeEventType = (
    eventType: string,
    sender?: string,
): { label: string; pillClass: string; borderClass: string } => {
    switch (eventType) {
        case 'human_characteristic_estimation':
            return {
                label: 'Human Characteristic Estimation',
                pillClass: 'bg-indigo-100 text-indigo-800',
                borderClass: 'border-indigo-500',
            };
        case 'unit_status_update':
            return {
                label: 'Unit Status Update',
                pillClass: 'bg-amber-100 text-amber-800',
                borderClass: 'border-amber-500',
            };
        case 'ai_operator':
            return {
                label: 'AI Operator',
                pillClass: 'bg-purple-100 text-purple-800',
                borderClass: 'border-purple-500',
            };
        case 'ai_summary':
            return {
                label: 'AI Summary',
                pillClass: 'bg-fuchsia-100 text-fuchsia-800',
                borderClass: 'border-fuchsia-500',
            };
        case 'characteristics_estimation':
            return {
                label: 'AI Characteristics Estimation',
                pillClass: 'bg-slate-100 text-slate-800',
                borderClass: 'border-slate-500',
            };
        case 'documentation_search':
            return {
                label: 'Documentation Search',
                pillClass: 'bg-cyan-100 text-cyan-800',
                borderClass: 'border-cyan-500',
            };
        case 'message':
        default:
            if (sender === 'ai_operator') {
                return {
                    label: 'AI Operator',
                    pillClass: 'bg-purple-100 text-purple-800',
                    borderClass: 'border-purple-500',
                };
            }
            return {
                label: 'Human Operator',
                pillClass: 'bg-emerald-100 text-emerald-800',
                borderClass: 'border-emerald-500',
            };
    }
};

interface LLMTransaction {
    id: number;
    llm_transaction_type: string;
    model: string;
    prompt_tokens: number | null;
    completion_tokens: number | null;
    total_tokens: number | null;
    input_token_cost: number | null;
    output_token_cost: number | null;
    total_cost: number | null;
    created_at: string;
}

interface MonthlyAggregate {
    year: number;
    month: number;
    month_name: string;
    total_tokens: number;
    total_cost: number;
    transaction_count: number;
}

interface MonthlyAggregateResponse {
    monthly_data: MonthlyAggregate[];
    current_month_cost: number;
}

type AdminTab = 'users' | 'feedback' | 'analytics' | 'situations' | 'phases_roles' | 'usage_and_cost' | 'api';

const ADMIN_TAB_FROM_URL: Record<string, AdminTab> = {
    users: 'users',
    feedback: 'feedback',
    analytics: 'analytics',
    situations: 'situations',
    simulaties: 'situations',
    phases_roles: 'phases_roles',
    usage_and_cost: 'usage_and_cost',
    api: 'api',
};

const AdminPanel = () => {
    const [searchParams] = useSearchParams();
    const [users, setUsers] = useState<VNOGUser[]>([]);
    const [invitations, setInvitations] = useState<UserInvitation[]>([]);
    const [loading, setLoading] = useState(true);
    const [newInvitationEmail, setNewInvitationEmail] = useState('');
    const [creatingInvitation, setCreatingInvitation] = useState(false);
    const [activeTab, setActiveTab] = useState<AdminTab>('users');

    useEffect(() => {
        const tabFromUrl = searchParams.get('tab');
        if (tabFromUrl && ADMIN_TAB_FROM_URL[tabFromUrl]) {
            setActiveTab(ADMIN_TAB_FROM_URL[tabFromUrl]);
        }
    }, [searchParams]);
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);
    const [loadingFeedback, setLoadingFeedback] = useState(false);
    const [feedbackTab, setFeedbackTab] = useState<'characteristics' | 'ai_operator' | 'human_suggested' | 'documentation'>('characteristics');
    const [situations, setSituations] = useState<Situation[]>([]);
    const [loadingSituations, setLoadingSituations] = useState(false);
    const [expandedSituations, setExpandedSituations] = useState<Set<number>>(new Set());
    const [loadingDetails, setLoadingDetails] = useState<Set<number>>(new Set());
    const [editingSituation, setEditingSituation] = useState<number | null>(null);
    const [editedEvents, setEditedEvents] = useState<Event[]>([]);
    const [editedSituationName, setEditedSituationName] = useState<string>('');
    const [savingSituation, setSavingSituation] = useState(false);
    const [uploadingIncidents, setUploadingIncidents] = useState(false);
    const [incidentFile, setIncidentFile] = useState<File | null>(null);
    const [showArchivedSituations, setShowArchivedSituations] = useState<boolean>(false);
    const [selectedSituationIds, setSelectedSituationIds] = useState<Set<number>>(new Set());
    const [bulkUpdatingArchived, setBulkUpdatingArchived] = useState<boolean>(false);
    const [llmTransactions, setLlmTransactions] = useState<LLMTransaction[]>([]);
    const [monthlyAggregate, setMonthlyAggregate] = useState<MonthlyAggregateResponse | null>(null);
    const [loadingLLMData, setLoadingLLMData] = useState(false);

    const fetchUsers = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/admin/users/`, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setUsers(data);
            } else {
                toast.error('Failed to fetch users');
            }
        } catch (error) {
            toast.error('Error fetching users');
        }
    };

    const fetchInvitations = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/admin/invitations/`, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setInvitations(data);
            } else {
                toast.error('Failed to fetch invitations');
            }
        } catch (error) {
            toast.error('Error fetching invitations');
        }
    };

    const fetchSituations = async (includeArchivedOverride?: boolean) => {
        const includeArchived = includeArchivedOverride ?? showArchivedSituations;
        setLoadingSituations(true);
        try {
            const url = includeArchived
                ? `${BACKEND_URL}/vnog/situations/?include_archived=true`
                : `${BACKEND_URL}/vnog/situations/`;
            const response = await fetch(url, { credentials: 'include' });
            if (response.ok) {
                const data = await response.json();
                setSituations(data);
                // Drop any selections that are no longer in the visible list.
                setSelectedSituationIds(prev => {
                    const visibleIds = new Set<number>((data as Situation[]).map(s => s.id));
                    const next = new Set<number>();
                    prev.forEach(id => { if (visibleIds.has(id)) next.add(id); });
                    return next;
                });
            } else {
                toast.error('Failed to fetch situations');
            }
        } catch (error) {
            toast.error('Error fetching situations');
        } finally {
            setLoadingSituations(false);
        }
    };

    const handleToggleShowArchived = () => {
        const next = !showArchivedSituations;
        setShowArchivedSituations(next);
        fetchSituations(next);
    };

    const handleToggleSituationSelection = (situationId: number) => {
        setSelectedSituationIds(prev => {
            const next = new Set(prev);
            if (next.has(situationId)) next.delete(situationId);
            else next.add(situationId);
            return next;
        });
    };

    const handleSelectAllSituations = () => {
        if (selectedSituationIds.size === situations.length) {
            setSelectedSituationIds(new Set());
        } else {
            setSelectedSituationIds(new Set(situations.map(s => s.id)));
        }
    };

    const handleBulkSetArchived = async (archived: boolean) => {
        if (selectedSituationIds.size === 0) return;
        const ids = Array.from(selectedSituationIds);
        setBulkUpdatingArchived(true);
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/situations/bulk_archive/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
                body: JSON.stringify({ situation_ids: ids, archived }),
            });
            if (response.ok) {
                const data = await response.json();
                const verb = archived ? 'archived' : 'restored';
                toast.success(`${data.updated} situation${data.updated === 1 ? '' : 's'} ${verb}`);
                setSelectedSituationIds(new Set());
                await fetchSituations();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Bulk update failed');
            }
        } catch (error) {
            toast.error('Bulk update failed');
        } finally {
            setBulkUpdatingArchived(false);
        }
    };

    const fetchSituationDetails = async (situationId: number): Promise<Situation | null> => {
        setLoadingDetails(prev => new Set(prev).add(situationId));
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/situations/${situationId}/`, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setSituations(prev => prev.map(s => s.id === situationId ? data : s));
                return data;
            } else {
                toast.error('Failed to fetch situation details');
                return null;
            }
        } catch (error) {
            toast.error('Error fetching situation details');
            return null;
        } finally {
            setLoadingDetails(prev => {
                const next = new Set(prev);
                next.delete(situationId);
                return next;
            });
        }
    };

    const handleToggleSituation = (situationId: number) => {
        const isExpanded = expandedSituations.has(situationId);
        if (isExpanded) {
            setExpandedSituations(prev => {
                const next = new Set(prev);
                next.delete(situationId);
                return next;
            });
        } else {
            setExpandedSituations(prev => new Set(prev).add(situationId));
            // Fetch details if not already loaded
            const situation = situations.find(s => s.id === situationId);
            if (situation && !situation.event_log) {
                fetchSituationDetails(situationId);
            }
        }
    };

    const handleEditSituation = async (situationId: number) => {
        const situation = situations.find(s => s.id === situationId);
        if (situation) {
            // Ensure we have the full details
            let situationToEdit = situation;
            if (!situation.event_log) {
                const fetchedSituation = await fetchSituationDetails(situationId);
                if (fetchedSituation) {
                    situationToEdit = fetchedSituation;
                } else {
                    return; // Failed to fetch
                }
            }
            
            if (situationToEdit.event_log) {
                setEditedEvents([...situationToEdit.event_log]);
                setEditedSituationName(situationToEdit.name || '');
                setEditingSituation(situationId);
            }
        }
    };

    const handleCancelEdit = () => {
        setEditingSituation(null);
        setEditedEvents([]);
        setEditedSituationName('');
    };

    const handleSaveSituation = async (situationId: number) => {
        setSavingSituation(true);
        try {
            // Prepare events data for the API
            const eventsData = editedEvents
                .filter(event => event.message_content) // Only include events with message content
                .map(event => ({
                    event_type: event.event_type || 'message',
                    message_content: event.message_content,
                    message_sender: event.message_sender || 'human_operator',
                    start_time: event.start_time || null,
                }));

            const response = await fetch(`${BACKEND_URL}/vnog/situations/${situationId}/edit/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
                body: JSON.stringify({
                    situation_id: situationId,
                    name: editedSituationName,
                    events: eventsData,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                // Update the situations list with the new situation
                setSituations(prev => {
                    // Remove old situation and add new one
                    const filtered = prev.filter(s => s.id !== situationId);
                    return [data, ...filtered];
                });
                toast.success('Situation saved successfully');
                handleCancelEdit();
                // Refresh the list to get updated data
                fetchSituations();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Failed to save situation');
            }
        } catch (error) {
            toast.error('Error saving situation');
        } finally {
            setSavingSituation(false);
        }
    };

    const handleUpdateEvent = (index: number, field: string, value: string) => {
        setEditedEvents(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handleAddEvent = () => {
        setEditedEvents(prev => [
            ...prev,
            {
                id: Date.now(), // Temporary ID
                event_type: 'message',
                message_content: '',
                message_sender: 'human_operator',
                start_time: new Date().toISOString(),
            } as Event,
        ]);
    };

    const handleRemoveEvent = (index: number) => {
        setEditedEvents(prev => prev.filter((_, i) => i !== index));
    };

    const handleDeleteSituation = async (situationId: number, situationName: string) => {
        if (!window.confirm(`Archive "${situationName || `Situation #${situationId}`}"? It will be hidden from the list but can be restored via "Show archived".`)) {
            return;
        }

        try {
            const response = await fetch(`${BACKEND_URL}/vnog/situations/${situationId}/delete/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
            });

            if (response.ok) {
                toast.success('Situation archived');
                fetchSituations();
                setExpandedSituations(prev => {
                    const next = new Set(prev);
                    next.delete(situationId);
                    return next;
                });
            } else {
                const error = await response.json();
                toast.error(error.error || 'Failed to archive situation');
            }
        } catch (error) {
            toast.error('Error archiving situation');
        }
    };

    const handleSetEventTimeToNow = (index: number) => {
        const now = new Date().toISOString();
        handleUpdateEvent(index, 'start_time', now);
    };

    const formatDateTimeForInput = (isoString: string | undefined): { date: string; time: string } => {
        if (!isoString) return { date: '', time: '' };
        try {
            const date = new Date(isoString);
            const dateStr = date.toISOString().split('T')[0];
            const timeStr = date.toTimeString().slice(0, 5); // HH:MM format
            return { date: dateStr, time: timeStr };
        } catch {
            return { date: '', time: '' };
        }
    };

    const handleDateTimeChange = (index: number, date: string, time: string) => {
        if (date && time) {
            const dateTime = new Date(`${date}T${time}`).toISOString();
            handleUpdateEvent(index, 'start_time', dateTime);
        } else if (date) {
            // If only date is provided, set time to 00:00
            const dateTime = new Date(`${date}T00:00`).toISOString();
            handleUpdateEvent(index, 'start_time', dateTime);
        } else {
            handleUpdateEvent(index, 'start_time', '');
        }
    };

    const handleIncidentFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setIncidentFile(file);
        }
    };

    const handleUploadIncidents = async () => {
        if (!incidentFile) {
            toast.error('Please select a JSON file');
            return;
        }

        setUploadingIncidents(true);
        try {
            const formData = new FormData();
            formData.append('file', incidentFile);

            const response = await fetch(`${BACKEND_URL}/vnog/situations/create/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
                body: formData,
            });

            if (response.ok) {
                const data = await response.json();
                const created: Situation[] = data.situations || [];
                if (created.length === 0) {
                    toast.error('No incidents were created from this file');
                } else if (created.length === 1) {
                    toast.success(`Created situation "${created[0].name || `#${created[0].id}`}"`);
                } else {
                    toast.success(`Created ${created.length} situations from incident file`);
                }
                setIncidentFile(null);
                const fileInput = document.getElementById('incident-upload-input') as HTMLInputElement;
                if (fileInput) {
                    fileInput.value = '';
                }
                fetchSituations();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Failed to upload incident file');
            }
        } catch (error) {
            toast.error('Error uploading incident file');
        } finally {
            setUploadingIncidents(false);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchUsers(), fetchInvitations()]);
            setLoading(false);
        };
        loadData();
    }, []);

    useEffect(() => {
        if (activeTab === 'situations') {
            fetchSituations();
        }
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'usage_and_cost') {
            fetchLLMData();
        }
    }, [activeTab]);

    const fetchLLMData = async () => {
        setLoadingLLMData(true);
        try {
            const [transactionsRes, aggregateRes] = await Promise.all([
                fetch(`${BACKEND_URL}/vnog/admin/llm-transactions/current-month/`, {
                    credentials: 'include',
                }),
                fetch(`${BACKEND_URL}/vnog/admin/llm-transactions/monthly-aggregate/`, {
                    credentials: 'include',
                }),
            ]);

            if (transactionsRes.ok) {
                const transactions = await transactionsRes.json();
                setLlmTransactions(transactions);
            } else {
                toast.error('Failed to fetch LLM transactions');
            }

            if (aggregateRes.ok) {
                const aggregate = await aggregateRes.json();
                setMonthlyAggregate(aggregate);
            } else {
                toast.error('Failed to fetch monthly aggregate');
            }
        } catch (error) {
            toast.error('Error fetching LLM data');
        } finally {
            setLoadingLLMData(false);
        }
    };

    const handleDisableUser = async (registryId: number, email: string) => {
        if (!window.confirm(`Are you sure you want to disable user ${email}?`)) {
            return;
        }

        try {
            const formData = new FormData();
            formData.append('registry_id', registryId.toString());

            const response = await fetch(`${BACKEND_URL}/vnog/admin/users/disable/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
                body: formData,
            });

            if (response.ok) {
                toast.success('User disabled successfully');
                fetchUsers();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Failed to disable user');
            }
        } catch (error) {
            toast.error('Error disabling user');
        }
    };

    const handleCreateInvitation = async () => {
        if (!newInvitationEmail.trim()) {
            toast.error('Please enter an email address');
            return;
        }

        setCreatingInvitation(true);
        try {
            const formData = new FormData();
            formData.append('email', newInvitationEmail.trim());

            const response = await fetch(`${BACKEND_URL}/vnog/admin/invitations/create/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
                body: formData,
            });

            if (response.ok) {
                const data = await response.json();
                const fullInvitationLink = `${window.location.origin}${data.invitation_link}`;
                toast.success(
                    <div>
                        <div>Invitation created successfully!</div>
                        <div className="mt-2">
                            <div className="text-sm text-gray-600 mb-1">Invitation link:</div>
                            <div className="bg-gray-100 p-2 rounded text-sm font-mono break-all">
                                {fullInvitationLink}
                            </div>
                            <button
                                onClick={() => copyToClipboard(fullInvitationLink)}
                                className="mt-2 text-blue-600 hover:text-blue-800 text-sm underline"
                            >
                                Copy Link
                            </button>
                        </div>
                    </div>,
                    { autoClose: 10000 }
                );

                const newInvitation: UserInvitation = {
                    id: data.id,
                    email: data.email,
                    token: data.token,
                    enabled: true,
                    status: 'enabled',
                    created_at: data.created_at,
                    created_by_email: 'You',
                    invitation_link: data.invitation_link,
                };
                setInvitations((prev) => [newInvitation, ...prev]);

                setNewInvitationEmail('');
            } else {
                const error = await response.json();
                toast.error(error.error || 'Failed to create invitation');
            }
        } catch (error) {
            toast.error('Error creating invitation');
        } finally {
            setCreatingInvitation(false);
        }
    };

    const handleToggleInvitation = async (invitationId: number, enabled: boolean) => {
        try {
            const formData = new FormData();
            formData.append('invitation_id', invitationId.toString());
            formData.append('enabled', enabled.toString());

            const response = await fetch(`${BACKEND_URL}/vnog/admin/invitations/toggle/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
                body: formData,
            });

            if (response.ok) {
                toast.success(`Invitation ${enabled ? 'enabled' : 'disabled'} successfully`);
                fetchInvitations();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Failed to update invitation');
            }
        } catch (error) {
            toast.error('Error updating invitation');
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.success('Link copied to clipboard');
        }).catch(() => {
            toast.error('Failed to copy link');
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-lg">Loading admin panel...</div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-6">
            <h1 className="text-3xl font-bold mb-6">Admin Panel</h1>

            <div className="mb-8 border-b border-gray-200">
                <nav className="-mb-px flex gap-6" aria-label="Tabs">
                    <button
                        className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${activeTab === 'users' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        onClick={() => setActiveTab('users')}
                    >
                        Users & Invitations
                    </button>
                    <button
                        className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${activeTab === 'feedback' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        onClick={() => setActiveTab('feedback')}
                    >
                        Feedback
                    </button>
                    <button
                        className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${activeTab === 'analytics' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        onClick={() => setActiveTab('analytics')}
                    >
                        Analytics
                    </button>
                    <button
                        className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${activeTab === 'situations' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        onClick={() => setActiveTab('situations')}
                    >
                        Situations
                    </button>
                    <button
                        className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${activeTab === 'phases_roles' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        onClick={() => setActiveTab('phases_roles')}
                    >
                        Phases & Roles
                    </button>
                    <button
                        className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${activeTab === 'usage_and_cost' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        onClick={() => setActiveTab('usage_and_cost')}
                    >
                        Usage and Cost
                    </button>
                    <button
                        className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${activeTab === 'api' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        onClick={() => setActiveTab('api')}
                    >
                        External API
                    </button>
                </nav>
            </div>

            {activeTab === 'users' && (
            <div className="mb-12">
                <h2 className="text-2xl font-semibold mb-4">VNOG Users</h2>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Type
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Created
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {users.map((user) => (
                                <tr key={user.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                            {user.email}
                                            {user.is_staff && (
                                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                    Staff
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            user.registry_type === 'admin' 
                                                ? 'bg-purple-100 text-purple-800' 
                                                : 'bg-blue-100 text-blue-800'
                                        }`}>
                                            {user.registry_type}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(user.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        {!user.is_staff && (
                                            <button
                                                onClick={() => handleDisableUser(user.id, user.email)}
                                                className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded-md text-sm"
                                            >
                                                Disable
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            )}

            {activeTab === 'users' && (
            <div>
                <h2 className="text-2xl font-semibold mb-4">User Invitations</h2>
                
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <h3 className="text-lg font-medium mb-4">Create New Invitation</h3>
                    <div className="flex gap-4">
                        <input
                            type="email"
                            value={newInvitationEmail}
                            onChange={(e) => setNewInvitationEmail(e.target.value)}
                            placeholder="Enter email address"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            onClick={handleCreateInvitation}
                            disabled={creatingInvitation}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            {creatingInvitation ? 'Creating...' : 'Create Invitation'}
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Created By
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Created
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {invitations.map((invitation) => (
                                <tr key={invitation.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {invitation.email}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            invitation.status === 'enabled' 
                                                ? 'bg-green-100 text-green-800' 
                                                : invitation.status === 'used'
                                                ? 'bg-blue-100 text-blue-800'
                                                : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {invitation.status === 'enabled' ? 'Active' : 
                                             invitation.status === 'used' ? 'Used' : 'Disabled'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {invitation.created_by_email}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(invitation.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                        <button
                                            onClick={() => copyToClipboard(`${window.location.origin}${invitation.invitation_link}`)}
                                            className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-md text-sm"
                                        >
                                            Copy Link
                                        </button>
                                        {invitation.status !== 'used' && (
                                            <button
                                                onClick={() => handleToggleInvitation(invitation.id, !invitation.enabled)}
                                                className={`px-3 py-1 rounded-md text-sm ${
                                                    invitation.enabled
                                                        ? 'text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100'
                                                        : 'text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100'
                                                }`}
                                            >
                                                {invitation.enabled ? 'Disable' : 'Enable'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            )}

            {activeTab === 'feedback' && (
            <div>
                <div className="mb-8 border-b border-gray-200">
                    <nav className="-mb-px flex gap-6" aria-label="Sub-Tabs">
                        <button
                            className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${feedbackTab === 'characteristics' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                            onClick={() => setFeedbackTab('characteristics')}
                        >
                            Characteristic Corrections
                        </button>
                        <button
                            className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${feedbackTab === 'ai_operator' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                            onClick={() => setFeedbackTab('ai_operator')}
                        >
                            AI Operator
                        </button>
                        <button
                            className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${feedbackTab === 'human_suggested' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                            onClick={() => setFeedbackTab('human_suggested')}
                        >
                            Human Suggested AI Operator Messages
                        </button>
                        <button
                            className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${feedbackTab === 'documentation' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                            onClick={() => setFeedbackTab('documentation')}
                        >
                            Documentation
                        </button>
                    </nav>
                </div>

                {feedbackTab === 'characteristics' && (
                    <CharacteristicCorrections onLoadingChange={setLoadingFeedback} />
                )}

                {feedbackTab === 'ai_operator' && (
                    <AIOperatorMessages onLoadingChange={setLoadingFeedback} />
                )}

                {feedbackTab === 'human_suggested' && (
                    <HumanSuggestedMessages onLoadingChange={setLoadingFeedback} />
                )}

                {feedbackTab === 'documentation' && (
                    <DocumentationFeedback onLoadingChange={setLoadingFeedback} />
                )}
            </div>
            )}

            {activeTab === 'analytics' && (
                <AnalyticsTab onLoadingChange={setLoadingAnalytics} />
            )}

            {activeTab === 'usage_and_cost' && (
            <div>
                <h2 className="text-2xl font-semibold mb-4">Usage and Cost</h2>
                
                {loadingLLMData ? (
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-center text-gray-600">Loading usage and cost data...</div>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Current Month Cost */}
                        {monthlyAggregate && (
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold mb-2">Current Month Cost</h3>
                                <div className="text-3xl font-bold text-blue-600">
                                    ${monthlyAggregate.current_month_cost.toFixed(4)}
                                </div>
                                <p className="text-sm text-gray-500 mt-1">
                                    Total cost for {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
                                </p>
                            </div>
                        )}

                        {/* Monthly Usage and Cost Charts */}
                        {monthlyAggregate && monthlyAggregate.monthly_data.length > 0 && (
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold mb-4">Monthly Usage and Cost</h3>
                                <div className="space-y-8">
                                    {/* Cost Chart */}
                                    <div>
                                        <h4 className="text-md font-medium mb-3 text-gray-700">Monthly Cost</h4>
                                        <div style={{ height: '300px' }}>
                                            <VictoryChart
                                                theme={VictoryTheme.material}
                                                domainPadding={20}
                                                height={300}
                                            >
                                                <VictoryAxis
                                                    tickFormat={(t) => {
                                                        const monthData = monthlyAggregate.monthly_data.find(m => m.month_name === t);
                                                        if (!monthData) return t;
                                                        const date = new Date(monthData.year, monthData.month - 1);
                                                        return date.toLocaleDateString('default', { month: 'short', year: '2-digit' });
                                                    }}
                                                    style={{
                                                        tickLabels: { angle: -45, fontSize: 10 }
                                                    }}
                                                />
                                                <VictoryAxis
                                                    dependentAxis
                                                    label="Cost ($)"
                                                    style={{
                                                        axisLabel: { padding: 35, fontSize: 12 }
                                                    }}
                                                />
                                                <VictoryBar
                                                    data={monthlyAggregate.monthly_data}
                                                    x="month_name"
                                                    y="total_cost"
                                                    style={{
                                                        data: { fill: "#3b82f6" }
                                                    }}
                                                />
                                            </VictoryChart>
                                        </div>
                                    </div>

                                    {/* Usage (Tokens) Chart */}
                                    <div>
                                        <h4 className="text-md font-medium mb-3 text-gray-700">Monthly Token Usage</h4>
                                        <div style={{ height: '300px' }}>
                                            <VictoryChart
                                                theme={VictoryTheme.material}
                                                domainPadding={20}
                                                height={300}
                                            >
                                                <VictoryAxis
                                                    tickFormat={(t) => {
                                                        const monthData = monthlyAggregate.monthly_data.find(m => m.month_name === t);
                                                        if (!monthData) return t;
                                                        const date = new Date(monthData.year, monthData.month - 1);
                                                        return date.toLocaleDateString('default', { month: 'short', year: '2-digit' });
                                                    }}
                                                    style={{
                                                        tickLabels: { angle: -45, fontSize: 10 }
                                                    }}
                                                />
                                                <VictoryAxis
                                                    dependentAxis
                                                    label="Tokens"
                                                    style={{
                                                        axisLabel: { padding: 35, fontSize: 12 }
                                                    }}
                                                    tickFormat={(t) => {
                                                        if (t >= 1000000) return `${(t / 1000000).toFixed(1)}M`;
                                                        if (t >= 1000) return `${(t / 1000).toFixed(1)}K`;
                                                        return t.toString();
                                                    }}
                                                />
                                                <VictoryBar
                                                    data={monthlyAggregate.monthly_data}
                                                    x="month_name"
                                                    y="total_tokens"
                                                    style={{
                                                        data: { fill: "#10b981" }
                                                    }}
                                                />
                                            </VictoryChart>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Current Month Transactions Table */}
                        {llmTransactions.length > 0 && (
                            <div className="bg-white rounded-lg shadow overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <h3 className="text-lg font-semibold">Current Month Transactions</h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Date
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Type
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Tokens
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Cost
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {llmTransactions.slice(0, 50).map((transaction) => (
                                                <tr key={transaction.id}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {new Date(transaction.created_at).toLocaleString()}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {transaction.llm_transaction_type.replace(/_/g, ' ')}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {transaction.total_tokens?.toLocaleString() || '-'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {transaction.total_cost ? `$${transaction.total_cost.toFixed(4)}` : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {llmTransactions.length > 50 && (
                                        <div className="px-6 py-4 bg-gray-50 text-sm text-gray-500">
                                            Showing first 50 of {llmTransactions.length} transactions
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {!loadingLLMData && monthlyAggregate && monthlyAggregate.monthly_data.length === 0 && llmTransactions.length === 0 && (
                            <div className="bg-white rounded-lg shadow p-6">
                                <p className="text-gray-600 text-center">No LLM transaction data available.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
            )}

            {activeTab === 'phases_roles' && (
                <PhasesRolesManager />
            )}

            {activeTab === 'api' && (
                <ExternalAPI />
            )}

            {activeTab === 'situations' && (
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-semibold">Situations</h2>
                </div>
                
                {/* Incident JSON Upload Section */}
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <h3 className="text-lg font-medium mb-4">Upload Incident JSON</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Incident JSON file
                                </label>
                                <input
                                    id="incident-upload-input"
                                    type="file"
                                    accept="application/json,.json"
                                    onChange={handleIncidentFileChange}
                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                />
                                {incidentFile && (
                                    <p className="mt-2 text-sm text-gray-600">
                                        Selected: {incidentFile.name}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={handleUploadIncidents}
                                disabled={uploadingIncidents || !incidentFile}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {uploadingIncidents ? 'Uploading...' : 'Upload incidents'}
                            </button>
                        </div>
                        <div className="bg-gray-50 rounded-md p-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Expected JSON shape</h4>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p className="mb-2">One upload can contain many incidents. Each becomes its own situation, named from <code className="bg-gray-200 px-1 rounded">title</code> and tagged with <code className="bg-gray-200 px-1 rounded">id_gms</code>.</p>
                                <ul className="list-disc list-inside space-y-1 ml-2">
                                    <li><code className="bg-gray-200 px-1 rounded">kladblok</code> entries become message events</li>
                                    <li><code className="bg-gray-200 px-1 rounded">karateristieken</code> become human characteristic estimations</li>
                                    <li><code className="bg-gray-200 px-1 rounded">eenheden</code> timestamps become unit status updates (null timestamps are skipped)</li>
                                    <li><code className="bg-gray-200 px-1 rounded">meldingsclassificatie</code> is placed first on the timeline</li>
                                </ul>
                                <div className="mt-4 pt-3 border-t border-gray-300">
                                    <p className="text-xs font-semibold text-gray-700 mb-2">Example:</p>
                                    <pre className="text-xs bg-white p-2 rounded border border-gray-300 overflow-x-auto">
{`[{"resultaat": [
  {
    "title": "Roken in woning leidt tot brand",
    "id_gms": 64291937,
    "eenheden": [...],
    "kladblok": [...],
    "karateristieken": [...],
    "meldingsclassificatie": [...]
  }
]}]`}
                                    </pre>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Selection + archive toolbar */}
                <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={situations.length > 0 && selectedSituationIds.size === situations.length}
                            onChange={handleSelectAllSituations}
                            disabled={situations.length === 0}
                        />
                        Select all
                    </label>
                    <span className="text-sm text-gray-500">
                        {selectedSituationIds.size} selected
                    </span>
                    <div className="flex gap-2 ml-2">
                        <button
                            onClick={() => handleBulkSetArchived(true)}
                            disabled={bulkUpdatingArchived || selectedSituationIds.size === 0}
                            className="px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {bulkUpdatingArchived ? 'Updating…' : 'Archive selected'}
                        </button>
                        <button
                            onClick={() => handleBulkSetArchived(false)}
                            disabled={bulkUpdatingArchived || selectedSituationIds.size === 0}
                            className="px-3 py-1.5 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {bulkUpdatingArchived ? 'Updating…' : 'Unarchive selected'}
                        </button>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 ml-auto">
                        <input
                            type="checkbox"
                            checked={showArchivedSituations}
                            onChange={handleToggleShowArchived}
                        />
                        Show archived
                    </label>
                </div>

                {loadingSituations ? (
                    <div className="px-6 py-4 bg-white rounded-lg shadow">Loading…</div>
                ) : situations.length === 0 ? (
                    <div className="px-6 py-4 bg-white rounded-lg shadow text-gray-500">No situations found</div>
                ) : (
                    <div className="space-y-4">
                        {situations.map((situation) => {
                            const isExpanded = expandedSituations.has(situation.id);
                            const isLoading = loadingDetails.has(situation.id);
                            const isSelected = selectedSituationIds.has(situation.id);
                            const messageEvents = situation.event_log?.filter(
                                (event) => event.message_content
                            ) || [];
                            
                            return (
                                <div key={situation.id} className={`bg-white rounded-lg shadow ${situation.archived ? 'opacity-75' : ''}`}>
                                    <div className="w-full p-6 flex items-start gap-4">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => handleToggleSituationSelection(situation.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="mt-1.5"
                                            aria-label={`Select ${situation.name || `Situation #${situation.gms_id}`}`}
                                        />
                                        <button
                                            onClick={() => handleToggleSituation(situation.id)}
                                            className="flex-1 text-left hover:bg-gray-50 transition-colors -m-2 p-2 rounded-md"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                                        {situation.name || `Situation #${situation.gms_id}`}
                                                        {situation.archived && (
                                                            <span className="text-xs font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                                                                Archived
                                                            </span>
                                                        )}
                                                    </h3>
                                                    <div className="text-sm text-gray-500 mt-1">
                                                        ID: {situation.id}
                                                        {situation.gms_id && (
                                                            <> • GMS: {situation.gms_id}</>
                                                        )}
                                                        {situation.event_log && (
                                                            <> • {messageEvents.length} message{messageEvents.length !== 1 ? 's' : ''}</>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="ml-4">
                                                    {isLoading ? (
                                                        <div className="text-sm text-gray-500">Loading...</div>
                                                    ) : (
                                                        <svg
                                                            className={`w-5 h-5 text-gray-500 transform transition-transform ${
                                                                isExpanded ? 'rotate-180' : ''
                                                            }`}
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    </div>

                                    {isExpanded && (
                                        <div className="px-6 pb-6 border-t border-gray-200 pt-4">
                                            {isLoading ? (
                                                <div className="text-sm text-gray-500 py-4">Loading messages...</div>
                                            ) : !situation.event_log ? (
                                                <div className="text-sm text-gray-500 py-4">No event log available</div>
                                            ) : editingSituation === situation.id ? (
                                                <div className="space-y-4">
                                                    {/* Sticky header bar with name and buttons */}
                                                    <div className="sticky top-0 z-10 bg-white border-b border-gray-200 pb-4 -mx-6 px-6 pt-4 -mt-4 mb-4">
                                                        <div className="flex items-end gap-4">
                                                            <div className="flex-1">
                                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                    Situation Name
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    value={editedSituationName}
                                                                    onChange={(e) => setEditedSituationName(e.target.value)}
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                />
                                                            </div>
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={() => handleSaveSituation(situation.id)}
                                                                    disabled={savingSituation}
                                                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                                                >
                                                                    {savingSituation ? 'Saving...' : 'Save'}
                                                                </button>
                                                                <button
                                                                    onClick={handleCancelEdit}
                                                                    disabled={savingSituation}
                                                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center justify-between mb-2">
                                                            <h4 className="text-sm font-medium text-gray-700">Events:</h4>
                                                            <button
                                                                onClick={handleAddEvent}
                                                                className="text-sm text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-md"
                                                            >
                                                                + Add Event
                                                            </button>
                                                        </div>
                                                        <div className="space-y-3">
                                                            {editedEvents.map((event, index) => {
                                                                const { date, time } = formatDateTimeForInput(event.start_time);
                                                                return (
                                                                    <div key={event.id || index} className="border border-gray-300 rounded-md p-4 bg-gray-50">
                                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                                                                            <div>
                                                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                                                    Date
                                                                                </label>
                                                                                <input
                                                                                    type="date"
                                                                                    value={date}
                                                                                    onChange={(e) => handleDateTimeChange(index, e.target.value, time)}
                                                                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                                />
                                                                            </div>
                                                                            <div>
                                                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                                                    Time
                                                                                </label>
                                                                                <div className="flex gap-2">
                                                                                    <input
                                                                                        type="time"
                                                                                        value={time}
                                                                                        onChange={(e) => handleDateTimeChange(index, date, e.target.value)}
                                                                                        className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                                    />
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => handleSetEventTimeToNow(index)}
                                                                                        className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md border border-gray-300 transition-colors"
                                                                                        title="Set to current time"
                                                                                    >
                                                                                        Now
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                            <div>
                                                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                                                    Sender
                                                                                </label>
                                                                                <select
                                                                                    value={event.message_sender || 'human_operator'}
                                                                                    onChange={(e) => handleUpdateEvent(index, 'message_sender', e.target.value)}
                                                                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                                >
                                                                                    <option value="human_operator">Human Operator</option>
                                                                                    <option value="ai_operator">AI Operator</option>
                                                                                </select>
                                                                            </div>
                                                                        </div>
                                                                    <div className="mb-3">
                                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                                            Message Content
                                                                        </label>
                                                                        <textarea
                                                                            value={event.message_content || ''}
                                                                            onChange={(e) => handleUpdateEvent(index, 'message_content', e.target.value)}
                                                                            rows={3}
                                                                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                        />
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handleRemoveEvent(index)}
                                                                        className="text-xs text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 py-1 rounded"
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h4 className="text-sm font-medium text-gray-700">Messages:</h4>
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => handleEditSituation(situation.id)}
                                                                className="text-sm text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-md"
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteSituation(situation.id, situation.name || '')}
                                                                className="text-sm text-amber-700 hover:text-amber-900 bg-amber-50 hover:bg-amber-100 px-3 py-1 rounded-md"
                                                            >
                                                                {situation.archived ? 'Archived' : 'Archive'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {messageEvents.length === 0 ? (
                                                        <div className="text-sm text-gray-500 italic">No events in this situation</div>
                                                    ) : (
                                                        <div className="space-y-2">
                                                            {messageEvents.map((event) => {
                                                                // Use created_at or start_time for timestamp instead of adjusted_start_time
                                                                const timestamp = event.start_time 
                                                                    ? new Date(event.start_time).toLocaleString()
                                                                    : event.adjusted_start_time || null;
                                                                const { label, pillClass, borderClass } = describeEventType(
                                                                    event.event_type,
                                                                    event.message_sender
                                                                );

                                                                return (
                                                                    <div
                                                                        key={event.id}
                                                                        className={`border-l-4 ${borderClass} pl-4 py-2 bg-gray-50 rounded-r`}
                                                                    >
                                                                        <div className="flex items-start justify-between mb-1">
                                                                            <div className="flex items-center gap-2">
                                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${pillClass}`}>
                                                                                    {label}
                                                                                </span>
                                                                            </div>
                                                                            {timestamp && (
                                                                                <span className="text-xs text-gray-500">
                                                                                    {timestamp}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="text-sm text-gray-900 mt-1 break-words">
                                                                            {event.message_content}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            )}
        </div>
    );
};

export default AdminPanel;